import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MAX_ADD_SETS, addSetLabel, addSetOptions, menuPlacement, setsToAdd, type MenuPlacement,
} from "../lib/addSets";

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * `Add Set` — THEIR SPLIT CONTROL, HIS ASK, FROM HIS OWN FOUR SCREENSHOTS (2026-09-19).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * One rounded outlined control, two halves with a hairline between them. Left: `Add Set`, adds
 * ONE. Right: a `+` glyph that opens a menu of `+ 2 Sets … + 8 Sets`, right-aligned under the
 * control. At rest both halves are grey on the card's surface; HOVER outlines the hovered HALF
 * ONLY in the accent with a faint accent fill — their blue is our `--ht-accent`, which is the
 * one thing about it that is not theirs and is the standing exception (tokens, themes, type).
 *
 * 🔴 **IT REPLACES OUR `+N ▾` SELECT, WHICH WAS THE SAME IDEA IN THE WRONG GRAMMAR.** A `<select>`
 * whose chosen value is immediately thrown away is a menu wearing a form control's clothes: it
 * announces itself as a combo box with a current value, it keeps no state worth having, and it
 * cannot draw the seven rows his stills show.
 *
 * ⚠️ **THE MENU ITEMS ARE REAL `<button>`s AND ESCAPE CLOSES THE MENU.** §12 measured their own
 * calendar at ZERO focusable elements; a menu copied from them is not a licence to copy that.
 * Focus returns to the `+` on close, because a menu that closes and leaves focus on the document
 * body strands a keyboard user at the top of the page.
 *
 * ⚠️ **A POINTER PRESS ANYWHERE ELSE CLOSES IT TOO.** A menu that can only be dismissed by
 * choosing something is a menu a coach has to obey.
 */
export function AddSetSplit({ onAdd, className }: {
  /** Called with the number of sets to add — already clamped by `setsToAdd`. */
  onAdd: (n: number) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<MenuPlacement>({ below: true, maxHeight: null });
  const root = useRef<HTMLDivElement>(null);
  const more = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);

  /* 🔴 THE MENU IS CLIPPED BY THE SCROLLER IT SITS IN, AND ONLY THE STILL SAID SO. The builder is
     a `<dialog>` whose body scrolls; theirs is a page. With the control near the foot of a card
     the menu was cut mid-way through `+ 6 Sets`. So it is MEASURED against the nearest scrolling
     ancestor once it has rendered — `useLayoutEffect`, before the browser paints, so the flip is
     never seen as a jump.
     ⚠️ AGAINST THE CLIP, NOT THE VIEWPORT: the window's body ended 90 px above the bottom of the
     screen in the very still that found this, so a viewport test would have said it fits. */
  useLayoutEffect(() => {
    if (!open) return;
    const el = menu.current;
    const anchor = root.current;
    if (el === null || anchor === null) return;
    /* ⚠️ MEASURED WITH THE CAP OFF. `scrollHeight` is what the seven rows WANT; reading
       `getBoundingClientRect().height` after a previous open had capped it would feed last
       time's answer back in and the menu would never grow again. */
    setPlace(menuPlacement(anchor.getBoundingClientRect(), clipRect(anchor), el.scrollHeight));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      /* ⚠️ `stopPropagation` — this control lives inside a `<dialog>` in the per-exercise editor,
         and Escape there closes the WINDOW. Closing the menu must not also close the window the
         coach is working in; the innermost thing open is the thing Escape dismisses. */
      e.stopPropagation();
      setOpen(false);
      more.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (root.current !== null && e.target instanceof Node && root.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  function add(n: number) {
    const many = setsToAdd(n);
    if (many === 0) return;
    onAdd(many);
  }

  return (
    <div className={className === undefined ? "addset" : `addset ${className}`} ref={root}>
      <button type="button" className="addset-one"
              onClick={() => { add(1); }}>
        Add Set
      </button>
      <button type="button" className="addset-more" ref={more}
              aria-haspopup="menu" aria-expanded={open}
              onClick={() => { setOpen((o) => !o); }}>
        <span aria-hidden="true">+</span>
        <span className="ht-visually-hidden">Add up to {MAX_ADD_SETS} sets</span>
      </button>
      {open && (
        <ul className={place.below ? "addset-menu" : "addset-menu is-above"} ref={menu}
            style={place.maxHeight === null ? undefined : { maxHeight: `${String(place.maxHeight)}px` }}
            role="menu" aria-label="How many sets to add">
          {addSetOptions().map((n) => (
            <li key={n} role="none">
              <button type="button" role="menuitem" className="addset-item"
                      onClick={() => { add(n); setOpen(false); more.current?.focus(); }}>
                {addSetLabel(n)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The box that will CUT this menu.
 *
 * 🔴 **THE INTERSECTION OF *EVERY* CLIPPING ANCESTOR, AND THE FIRST CUT TOOK THE NEAREST ONE.**
 * MEASURED live: the nearest was `.builder-section`, bottom **1022**, so the rule answered "it
 * fits" about a menu ending at 933 — while `.builder-window-body`, two levels further up, ended
 * at **910** and was the box actually doing the cutting. A stack of scrollers clips to the
 * overlap of all of them, not to the innermost.
 *
 * ⚠️ `overflow: hidden` COUNTS. It clips exactly as hard as `auto` does and is the commoner
 * spelling on a card; a rule that only looked for scrollers would answer "the viewport" about a
 * box that was already cutting the menu off.
 */
function clipRect(from: Element): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  let el: Element | null = from.parentElement;
  while (el !== null) {
    const s = getComputedStyle(el);
    if (/(auto|scroll|hidden)/.test(s.overflowY)) {
      const r = el.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
    el = el.parentElement;
  }
  return { top, bottom };
}
